import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../modeSelect/modeSelect.css";
import "./activityBuilder.css";
import {
  fetchSession,
  addActivity,
  updateActivity,
  deleteActivity,
  reorderActivities,
  setActivitiesLocal,
  selectSession,
  selectActivities,
} from "../../sessionBuilderSlice";

// Activity type metadata. `live` flag = only available in Live mode.
const ACTIVITY_TYPES = [
  { key: "quiz", label: "Quiz", icon: "fa-solid fa-circle-question", live: false },
  { key: "poll", label: "Poll", icon: "fa-solid fa-chart-simple", live: true },
  { key: "wordcloud", label: "Word Cloud", icon: "fa-solid fa-cloud", live: true },
  { key: "pindrop", label: "Pin Drop", icon: "fa-solid fa-location-dot", live: true },
];

const EDITOR_ROUTE = {
  quiz: "quiz",
  poll: "poll",
  wordcloud: "wordcloud",
  pindrop: "pindrop",
};

const TIMER_MIN = 10; // 00:10
const TIMER_MAX = 180; // 03:00

const ActivityBuilder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSelector(selectSession);
  const activities = useSelector(selectActivities);

  const [showPicker, setShowPicker] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [timerDrafts, setTimerDrafts] = useState({}); // {activityId: "mm:ss" being typed}

  useEffect(() => {
    dispatch(fetchSession());
  }, [dispatch]);

  const isLive = (session?.mode ?? "live") === "live";

  const typeMeta = (type) => ACTIVITY_TYPES.find((t) => t.key === type) || {};

  const handleAdd = async (type) => {
    setShowPicker(false);
    try {
      const meta = typeMeta(type);
      await dispatch(addActivity({ type, title: `${meta.label}`, timer_seconds: 30 })).unwrap();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to add activity", "error");
    }
  };

  // While typing we hold the raw mm:ss text per-activity, so the field never
  // snaps mid-entry. On blur we parse + clamp to 00:10..03:00 and commit.
  const setTimerDraft = (id, value) =>
    setTimerDrafts((prev) => ({ ...prev, [id]: value }));

  const commitTimer = (activity) => {
    const raw = timerDrafts[activity.id];
    let secs = raw === undefined ? activity.timer_seconds : parseTimer(raw);
    secs = Math.min(TIMER_MAX, Math.max(TIMER_MIN, secs));
    if (secs !== activity.timer_seconds) {
      dispatch(
        updateActivity({
          activity_id: activity.id,
          title: activity.title,
          timer_seconds: secs,
        })
      );
    }
    // Drop the draft so the field re-derives the clamped mm:ss from state.
    setTimerDrafts((prev) => {
      const next = { ...prev };
      delete next[activity.id];
      return next;
    });
  };

  const handleTitleChange = (activity, value) => {
    dispatch(
      updateActivity({
        activity_id: activity.id,
        title: value,
        timer_seconds: activity.timer_seconds,
      })
    );
  };

  const handleDelete = (activity) => {
    Swal.fire({
      title: "Delete activity?",
      text: `"${activity.title || typeMeta(activity.type).label}" and its content will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then((r) => {
      if (r.isConfirmed) dispatch(deleteActivity({ activity_id: activity.id }));
    });
  };

  const handleDeleteAll = async () => {
    if (activities.length === 0) return;
    const confirm = await Swal.fire({
      title: "Delete all activities?",
      html: `This permanently removes all <b>${activities.length}</b> activit${
        activities.length === 1 ? "y" : "ies"
      } and their content (questions, options, prompts, images). This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete All",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d23b3b",
    });
    if (!confirm.isConfirmed) return;

    try {
      // Sequential delete (backend cascades each activity's content via FK).
      for (const a of [...activities]) {
        // eslint-disable-next-line no-await-in-loop
        await dispatch(deleteActivity({ activity_id: a.id })).unwrap();
      }
      Swal.fire("Deleted", "All activities have been removed.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to delete all activities", "error");
    }
  };

  const openEditor = (activity) => {
    navigate(`/admin/session/activities/${EDITOR_ROUTE[activity.type]}/${activity.id}`);
  };

  // --- Drag reorder (native HTML5 DnD) ---
  const onDragStart = (idx) => setDragIndex(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (idx) => {
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null);
      return;
    }
    const next = [...activities];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(idx, 0, moved);
    dispatch(setActivitiesLocal(next.map((a, i) => ({ ...a, sort_order: i }))));
    dispatch(reorderActivities({ ordered_ids: next.map((a) => a.id) }));
    setDragIndex(null);
  };

  // mm:ss display, e.g. 30 -> "00:30", 90 -> "01:30".
  const formatTimer = (s) => {
    const sec = Math.max(0, Number(s) || 0);
    const m = Math.floor(sec / 60);
    const r = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  };

  // Parse a user-typed mm:ss (or bare seconds) back to total seconds.
  const parseTimer = (str) => {
    const t = String(str).trim();
    if (t.includes(":")) {
      const [mm, ss] = t.split(":");
      const m = parseInt(mm, 10) || 0;
      const sObj = parseInt(ss, 10) || 0;
      return m * 60 + sObj;
    }
    return parseInt(t, 10) || 0;
  };

  return (
    <>
      <div className="rules-page-new">
        <div className="admin-tabs-holder-v2">
          <div className="tabs-main-title">
            Activities
            <span className="mode-pill">{isLive ? "Live" : "Self-Paced"}</span>
          </div>

          <div className="ab-toolbar">
            <div className="ab-count">
              {activities.length} {activities.length === 1 ? "activity" : "activities"}
            </div>
            <div className="ab-toolbar-actions">
              {activities.length > 0 && (
                <button className="ab-delete-all" onClick={handleDeleteAll}>
                  <i className="fa-solid fa-trash"></i> Delete All
                </button>
              )}
              <div className="ab-add-wrap">
                <button className="ss-upload-btn" onClick={() => setShowPicker((v) => !v)}>
                  <i className="fa-solid fa-plus"></i> &nbsp; Add Activity
                </button>
                {showPicker && (
                  <div className="ab-picker">
                    {ACTIVITY_TYPES.map((t) => {
                      const disabled = !isLive && t.live;
                      return (
                        <div
                          key={t.key}
                          className={`ab-picker-item ${disabled ? "ab-picker-item-disabled" : ""}`}
                          onClick={() => !disabled && handleAdd(t.key)}
                          title={disabled ? "Only available in Live mode" : undefined}
                        >
                          <i className={t.icon}></i>
                          <span>{t.label}</span>
                        </div>
                      );
                    })}
                    {!isLive && (
                      <div className="ab-picker-note">
                        Self-Paced supports Quiz only.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="ab-timer-hint code">
            Time per question: min {formatTimer(TIMER_MIN)} — max {formatTimer(TIMER_MAX)} (mm:ss)
          </div>

          {activities.length === 0 ? (
            <div className="admin-tab-container ab-empty">
              <i className="fa-solid fa-layer-group"></i>
              <p>No activities yet. Add your first activity to get started.</p>
            </div>
          ) : (
            <div className="ab-list">
              {activities.map((a, idx) => {
                const meta = typeMeta(a.type);
                return (
                  <div
                    key={a.id}
                    className={`ab-item ${dragIndex === idx ? "ab-item-dragging" : ""}`}
                    draggable
                    onDragStart={() => onDragStart(idx)}
                    onDragOver={onDragOver}
                    onDrop={() => onDrop(idx)}
                  >
                    <div className="ab-drag-handle" title="Drag to reorder">
                      <i className="fa-solid fa-grip-vertical"></i>
                    </div>
                    <div className="ab-order">{idx + 1}</div>
                    <div className={`ab-type ab-type-${a.type}`}>
                      <i className={meta.icon}></i>
                      <span>{meta.label}</span>
                    </div>
                    <input
                      className="ab-title-input"
                      value={a.title || ""}
                      placeholder={`${meta.label} title`}
                      onChange={(e) => handleTitleChange(a, e.target.value)}
                    />
                    <div className="ab-timer" title="Time per question (mm:ss) — applied to every question/item in this activity. Min 00:10, max 03:00.">
                      <i className="fa-solid fa-clock"></i>
                      <input
                        className="ab-timer-input"
                        type="text"
                        inputMode="numeric"
                        placeholder="mm:ss"
                        value={timerDrafts[a.id] ?? formatTimer(a.timer_seconds)}
                        onChange={(e) => setTimerDraft(a.id, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onBlur={() => commitTimer(a)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      />
                      <span className="ab-timer-label">min / question</span>
                    </div>
                    <div className="ab-actions">
                      <button className="ab-edit" onClick={() => openEditor(a)} title="Edit content">
                        <i className="fa-solid fa-pen"></i> Content
                      </button>
                      <button className="ab-delete" onClick={() => handleDelete(a)} title="Delete">
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="rules-action-holder">
            <button
              className="save-and-continue"
              onClick={() => navigate("/admin/session/review")}
            >
              Finish & Review
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActivityBuilder;
